import React, { useMemo } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import clsx from 'clsx';
import { OUTREACH_CONTACT_KINDS, type OutreachContactKind } from 'fym-shared/src/music24/outreach/types';
import { TypedMemo } from '../../../common/types';
import type { ContactCounts, ContactFilters } from '../hooks/useContacts';
import { CONTACT_KIND_LABEL } from '../outreachLabels';
import { contactsMessages as messages } from './contactsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
/* eslint-enable css-modules/no-unused-class */

type ChipKey = 'all' | 'replied' | 'placed' | 'imported' | 'doNotContact';

const CHIPS: Array<{ key: ChipKey; label: MessageDescriptor; patch: Partial<ContactFilters> }> = [
  { key: 'all', label: messages.chipAll, patch: { status: null, source: null } },
  { key: 'replied', label: messages.chipReplied, patch: { status: 'replied', source: null } },
  { key: 'placed', label: messages.chipPlaced, patch: { status: 'placed', source: null } },
  { key: 'imported', label: messages.chipImported, patch: { status: null, source: 'imported' } },
  { key: 'doNotContact', label: messages.chipNeverContact, patch: { status: 'doNotContact', source: null } },
];

/** The chip of the filters: "Your imports" first, then the status. */
export function activeContactChip(filters: ContactFilters): ChipKey {
  if (filters.source === 'imported') {
    return 'imported';
  }
  if (filters.status === 'replied' || filters.status === 'placed' || filters.status === 'doNotContact') {
    return filters.status;
  }
  return 'all';
}

interface ContactsFilterBarProps {
  filters: ContactFilters;
  counts: ContactCounts;
  onChange: (patch: Partial<ContactFilters>) => void;
}

/**
 * The filters of Contacts (design outreach-v2 k1): All, Replied, Added a song, Your imports, Never
 * contact, each with its count, and the kind of person.
 */
const ContactsFilterBarComponent: React.FC<ContactsFilterBarProps> = ({ filters, counts, onChange }) => {
  const intl = useIntl();
  const active = activeContactChip(filters);
  const chips = useMemo(
    () =>
      CHIPS.map((chip) => (
        <button
          key={chip.key}
          type="button"
          className={clsx(shared.chip, shared.chipButton, active === chip.key && shared.chipOn)}
          aria-pressed={active === chip.key}
          onClick={() => onChange({ ...chip.patch, listUuid: null })}
          data-testid={`contacts-chip-${chip.key}`}
        >
          {intl.formatMessage(chip.label)} <b>{intl.formatNumber(counts[chip.key])}</b>
        </button>
      )),
    [active, counts, intl, onChange]
  );
  const kinds = useMemo(
    () =>
      OUTREACH_CONTACT_KINDS.map((kind) => (
        <option key={kind} value={kind}>
          {intl.formatMessage(CONTACT_KIND_LABEL[kind])}
        </option>
      )),
    [intl]
  );

  return (
    <div className={clsx(shared.row, 'flex-wrap')} data-testid="contacts-filter-bar">
      {chips}
      <select
        className={clsx(shared.input, 'w-auto')}
        value={filters.kind ?? ''}
        aria-label={intl.formatMessage(messages.kindFilter)}
        onChange={(event) =>
          onChange({ kind: event.target.value === '' ? null : (event.target.value as OutreachContactKind) })
        }
      >
        <option value="">{intl.formatMessage(messages.allKinds)}</option>
        {kinds}
      </select>
    </div>
  );
};

export const ContactsFilterBar = TypedMemo(ContactsFilterBarComponent);
